// Shared (client-safe) types and validation for public Palworld TCG player
// profiles. No Supabase import here so route files can use the types without
// pulling server code into the browser bundle.
import { z } from "zod";

export type Playstyle = "collector" | "player" | "investor" | "mixed";

export const PLAYSTYLES: { value: Playstyle; label: string; blurb: string }[] = [
  { value: "collector", label: "Collector", blurb: "Chasing full sets and clean copies" },
  { value: "player", label: "Player", blurb: "Building decks and playing matches" },
  { value: "investor", label: "Investor", blurb: "Tracking sealed and graded value" },
  { value: "mixed", label: "Collector + player", blurb: "A bit of everything" },
];

/** Public shape of a profile. Never carries an account id or email. */
export interface PlayerProfile {
  handle: string;
  display_name: string;
  bio: string;
  region: string;
  playstyle: Playstyle;
  favorite_pal: string;
  favorite_card: string;
  trade_ready: boolean;
  published: boolean;
  updated_at: string;
  /** Counts derived server-side from the member's own want-list listings. */
  wants: number;
  haves: number;
  /** Exact printed card codes the member is hunting / has spare. */
  want_codes: string[];
  have_codes: string[];
}

export const HANDLE_PATTERN = /^[a-z0-9-]{3,24}$/;

export const profileInputSchema = z.object({
  handle: z
    .string()
    .trim()
    .toLowerCase()
    .regex(HANDLE_PATTERN, "Use 3-24 lowercase letters, numbers or dashes."),
  displayName: z.string().trim().min(1).max(40),
  bio: z.string().trim().max(400).default(""),
  region: z.string().trim().max(40).default(""),
  playstyle: z.enum(["collector", "player", "investor", "mixed"]),
  favoritePal: z.string().trim().max(60).default(""),
  favoriteCard: z.string().trim().max(24).default(""),
  tradeReady: z.boolean().default(true),
  published: z.boolean().default(true),
});

export type ProfileInput = z.infer<typeof profileInputSchema>;

export const handleSchema = z.object({ handle: z.string().trim().toLowerCase().max(24) });

export function playstyleLabel(style: Playstyle): string {
  return PLAYSTYLES.find((p) => p.value === style)?.label ?? "Collector";
}

/** Suggest a URL handle from a display name. */
export function suggestHandle(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 24);
}
